import { useState } from 'react';
import type { FormEvent } from 'react';
import { Alert, Box, Button, Card, CardContent, Stack, TextField, Typography } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';

export default function PasswordResetConfirmForm() {
  const { confirmPasswordReset, authState } = useAuth();
  const [params] = useSearchParams();
  const key = params.get('key') || '';
  const [password, setPassword] = useState<string>('');
  const [confirmation, setConfirmation] = useState<string>('');
  const [mismatch, setMismatch] = useState<boolean>(false);
  const [completeKey, setCompleteKey] = useState<string>('');
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (authState.isLoading || !key) return;
    setMismatch(password !== confirmation);
    if (password !== confirmation) return;
    const result = await confirmPasswordReset({ key, password1: password, password2: confirmation });
    if (result.success) {
      setCompleteKey(key);
      setPassword('');
      setConfirmation('');
    }
  };
  return (
    <Box sx={{ maxWidth: 640, mx: 'auto', p: { xs: 2, md: 3 } }}>
      <Card>
        <CardContent>
          <Stack spacing={3}>
            <Typography variant="h4" component="h1">
              Reset Password
            </Typography>
            {key && completeKey === key ? (
              <>
                <Alert severity="success">Your password has been reset. Sign in with your new password.</Alert>
                <Button component="a" href="/accounts/login/" variant="contained">
                  Sign In
                </Button>
              </>
            ) : !key ? (
              <>
                <Alert severity="warning">This link is missing a reset key. Request a new password reset link.</Alert>
                <Button component="a" href="/auth/password-reset" variant="contained">
                  Request Reset Link
                </Button>
              </>
            ) : (
              <Box component="form" onSubmit={submit}>
                <Stack spacing={2}>
                  <TextField
                    label="New Password"
                    type="password"
                    required
                    fullWidth
                    autoComplete="new-password"
                    value={password}
                    disabled={authState.isLoading}
                    onChange={(event) => setPassword(event.target.value)}
                  />
                  <TextField
                    label="Confirm New Password"
                    type="password"
                    required
                    fullWidth
                    autoComplete="new-password"
                    value={confirmation}
                    disabled={authState.isLoading}
                    error={mismatch}
                    helperText={mismatch ? 'Passwords do not match.' : undefined}
                    onChange={(event) => {
                      setConfirmation(event.target.value);
                      setMismatch(false);
                    }}
                  />
                  {authState.error && <Alert severity="error">{authState.error}</Alert>}
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={authState.isLoading}
                    loading={authState.isLoading}
                    loadingPosition="start"
                  >
                    {authState.isLoading ? 'Resetting Password…' : 'Reset Password'}
                  </Button>
                </Stack>
              </Box>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
